/**
 * Vuetify icon set that draws `mdi-*` names with Phosphor Regular.
 *
 * Registered as the `mdi` set (the default), so every `<v-icon icon="mdi-x">`,
 * `prepend-icon`, `append-inner-icon`, `<v-icon>mdi-x</v-icon>` and Vuetify's
 * own `$close` / `$dropdown` / `$prev` … aliases go through here. A name with
 * no Phosphor mapping renders exactly what Vuetify's mdi set renders
 * (`<i class="mdi mdi-x">` from the MDI webfont), so nothing goes blank.
 *
 * The <svg> sits inside the same element Vuetify gives a font icon and takes
 * Vuetify's `.v-icon__svg` class (100% of a 1em box, fill: currentColor), so
 * the `size` prop, density and x-small … x-large keep working unchanged.
 */
import { defineComponent, h, type PropType } from 'vue';
import type { IconAliases, IconSet } from 'vuetify';
import { aliases as mdiAliases } from 'vuetify/iconsets/mdi';
import { mdiToPhosphor, parseMdi } from './mdiMap';
import { phosphorInner } from './phosphor';
import './phosphor.css';

export const PhosphorMdiIcon = defineComponent({
    name: 'PhosphorMdiIcon',
    props: {
        icon: { type: [String, Function, Object, Array] as PropType<unknown> },
        tag: { type: String, required: true }
    },
    setup(props) {
        return () => {
            const icon = typeof props.icon === 'string' ? props.icon : '';
            const { name, extraClasses } = parseMdi(icon);
            const ph = name ? mdiToPhosphor(name) : null;
            const inner = ph ? phosphorInner(ph) : null;
            // Same markup as Vuetify's own mdi set: the webfont draws it.
            if (inner === null) return h(props.tag, { class: ['mdi', icon] });
            // extraClasses keeps helpers such as `mdi-spin` (see phosphor.css)
            // and any custom class that rode along in the icon string.
            return h(props.tag, { class: ['bs-ph-icon', `bs-ph-${ph}`, ...extraClasses] }, [
                h('svg', {
                    class: 'v-icon__svg',
                    xmlns: 'http://www.w3.org/2000/svg',
                    viewBox: '0 0 256 256',
                    fill: 'currentColor',
                    'aria-hidden': 'true',
                    focusable: 'false',
                    innerHTML: inner
                })
            ]);
        };
    }
});

export const phosphorMdiSet: IconSet = { component: PhosphorMdiIcon as unknown as IconSet['component'] };

/**
 * Vuetify's internal aliases. Most stay MDI names and so pick up their Phosphor
 * mapping ($close -> x, $dropdown -> caret-down, $prev -> caret-left …).
 *
 * Kept on the MDI font on purpose, via Vuetify's built-in `class:` set:
 * - checkbox and radio states: MDI draws a SOLID box/dot for "on" and an empty
 *   one for "off". Phosphor Regular check-square / radio-button are outlines
 *   either way, so a ticked and an unticked control would read almost alike.
 * - rating stars: Regular has one outline star, so full and empty would match.
 * - carousel delimiter: a filled dot.
 */
export const phosphorAliases: IconAliases = {
    ...mdiAliases,
    checkboxOn: 'class:mdi mdi-checkbox-marked',
    checkboxOff: 'class:mdi mdi-checkbox-blank-outline',
    checkboxIndeterminate: 'class:mdi mdi-minus-box',
    radioOn: 'class:mdi mdi-radiobox-marked',
    radioOff: 'class:mdi mdi-radiobox-blank',
    ratingEmpty: 'class:mdi mdi-star-outline',
    ratingFull: 'class:mdi mdi-star',
    ratingHalf: 'class:mdi mdi-star-half-full',
    delimiter: 'class:mdi mdi-circle'
};

/**
 * MDI icons written as raw markup, `<i class="mdi mdi-x">`, outside <v-icon>
 * (BsSelect's search glyph, the schedule week-summary stats). Vuetify never
 * sees these, so they are swapped with CSS instead: the font glyph's ::before
 * is replaced by a currentColor box masked with the Phosphor SVG. Only these
 * names get a rule; one per mapped name would add ~400 KB of CSS.
 */
export const RAW_MDI_CLASS_NAMES = [
    'magnify',
    'account-group-outline',
    'wrench-outline',
    'clock-outline',
    'currency-usd',
    'chart-box-outline'
];

export function rawMdiClassCss(): string {
    return RAW_MDI_CLASS_NAMES.map((name) => {
        const ph = mdiToPhosphor(name);
        const inner = ph ? phosphorInner(ph) : null;
        if (inner === null) return '';
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">${inner}</svg>`;
        const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
        return (
            `.mdi.mdi-${name}::before{content:"";display:inline-block;width:1em;height:1em;` +
            `vertical-align:-0.125em;background-color:currentColor;` +
            `-webkit-mask:${url} center/contain no-repeat;mask:${url} center/contain no-repeat}`
        );
    }).join('\n');
}

/** Adds the raw-class rules once. No-op outside a browser (unit tests, SSR). */
export function installRawMdiClassCss(): void {
    if (typeof document === 'undefined' || document.getElementById('bs-ph-raw-mdi')) return;
    const style = document.createElement('style');
    style.id = 'bs-ph-raw-mdi';
    style.textContent = rawMdiClassCss();
    document.head.appendChild(style);
}
